/**
 * Central enums. Every status, event, job type and actor used anywhere in the system
 * comes from here (no magic strings, §101). Each enum is exported three ways:
 *
 *  - `Foo`        – `as const` object, use as value: `Foo.BAR`
 *  - `type Foo`   – string literal union
 *  - `FooSchema`  – Zod enum for validation
 *  - `FOO_VALUES` – readonly tuple (stable order, used for DB CHECK constraints and UI lists)
 *
 * The SQL `CHECK (status IN (…))` constraints in `packages/db/migrations/0001_initial.sql`
 * mirror these values; a unit test in `packages/db` asserts that they stay in sync.
 */
import { z } from 'zod';

function values<T extends Record<string, string>>(obj: T): readonly [T[keyof T], ...T[keyof T][]] {
  return Object.values(obj) as unknown as readonly [T[keyof T], ...T[keyof T][]];
}

/** §9 – lifecycle of a single activation code (one seat). */
export const ActivationCodeStatus = {
  AVAILABLE: 'AVAILABLE',
  PENDING_EMAIL: 'PENDING_EMAIL',
  CLAIMED: 'CLAIMED',
  PROVISIONING: 'PROVISIONING',
  REDEEMED: 'REDEEMED',
  DISABLED: 'DISABLED',
} as const;
export type ActivationCodeStatus = (typeof ActivationCodeStatus)[keyof typeof ActivationCodeStatus];
export const ACTIVATION_CODE_STATUS_VALUES = values(ActivationCodeStatus);
export const ActivationCodeStatusSchema = z.enum(ActivationCodeStatus);

/** §12 – lifecycle of a registration (one participant's attempt to redeem a code). */
export const RegistrationStatus = {
  PENDING_EMAIL: 'PENDING_EMAIL',
  EMAIL_VERIFIED: 'EMAIL_VERIFIED',
  PROVISIONING: 'PROVISIONING',
  ACTIVE: 'ACTIVE',
  PROVISIONING_FAILED: 'PROVISIONING_FAILED',
  EXPIRED: 'EXPIRED',
  CANCELLED: 'CANCELLED',
} as const;
export type RegistrationStatus = (typeof RegistrationStatus)[keyof typeof RegistrationStatus];
export const REGISTRATION_STATUS_VALUES = values(RegistrationStatus);
export const RegistrationStatusSchema = z.enum(RegistrationStatus);

/** §31 – delivery state of the latest verification email of a registration. */
export const EmailDeliveryStatus = {
  UNKNOWN: 'UNKNOWN',
  SENT: 'SENT',
  DELIVERED: 'DELIVERED',
  BOUNCED: 'BOUNCED',
  COMPLAINED: 'COMPLAINED',
} as const;
export type EmailDeliveryStatus = (typeof EmailDeliveryStatus)[keyof typeof EmailDeliveryStatus];
export const EMAIL_DELIVERY_STATUS_VALUES = values(EmailDeliveryStatus);
export const EmailDeliveryStatusSchema = z.enum(EmailDeliveryStatus);

/** Batch lifecycle. Codes are generated asynchronously in chunks (10 ms CPU limit, ADR 019). */
export const BatchStatus = {
  GENERATING: 'GENERATING',
  READY: 'READY',
  DISABLED: 'DISABLED',
} as const;
export type BatchStatus = (typeof BatchStatus)[keyof typeof BatchStatus];
export const BATCH_STATUS_VALUES = values(BatchStatus);
export const BatchStatusSchema = z.enum(BatchStatus);

/** Outbox / integration job lifecycle (ADR 016). */
export const IntegrationJobStatus = {
  /** Persisted in the outbox, not yet handed to the queue (or queue send failed). */
  PENDING: 'PENDING',
  /** `queue.send` succeeded. */
  DISPATCHED: 'DISPATCHED',
  /** A consumer claimed the job via CAS DISPATCHED|FAILED_RETRYABLE → RUNNING. */
  RUNNING: 'RUNNING',
  SUCCEEDED: 'SUCCEEDED',
  /** Last attempt failed with a retryable error; `next_attempt_at` is set. */
  FAILED_RETRYABLE: 'FAILED_RETRYABLE',
  /** Permanently failed or attempts exhausted; visible on the admin operations page. */
  DEAD: 'DEAD',
} as const;
export type IntegrationJobStatus = (typeof IntegrationJobStatus)[keyof typeof IntegrationJobStatus];
export const INTEGRATION_JOB_STATUS_VALUES = values(IntegrationJobStatus);
export const IntegrationJobStatusSchema = z.enum(IntegrationJobStatus);

export const IntegrationJobType = {
  GENERATE_BATCH_CODES: 'GENERATE_BATCH_CODES',
  PROVISION_LEARNINGSUITE: 'PROVISION_LEARNINGSUITE',
  SEND_VERIFICATION_EMAIL: 'SEND_VERIFICATION_EMAIL',
  SEND_ADMIN_LOGIN_EMAIL: 'SEND_ADMIN_LOGIN_EMAIL',
  /** Renders ANY generated document (BATCH_PDF, BATCH_ZIP, CODE_PDF, BATCH_CSV parts – ADR 066). */
  RENDER_BATCH_PDF: 'RENDER_BATCH_PDF',
  SYNC_BATCH_SHEET: 'SYNC_BATCH_SHEET',
} as const;
export type IntegrationJobType = (typeof IntegrationJobType)[keyof typeof IntegrationJobType];
export const INTEGRATION_JOB_TYPE_VALUES = values(IntegrationJobType);
export const IntegrationJobTypeSchema = z.enum(IntegrationJobType);

/** Status of a generated document (PDF/ZIP) in `generated_documents`. */
export const DocumentStatus = {
  PENDING: 'PENDING',
  READY: 'READY',
  FAILED: 'FAILED',
} as const;
export type DocumentStatus = (typeof DocumentStatus)[keyof typeof DocumentStatus];
export const DOCUMENT_STATUS_VALUES = values(DocumentStatus);
export const DocumentStatusSchema = z.enum(DocumentStatus);

/** Kind of generated document (§43, §44, §39 „PDF nur für diesen Code“). */
export const DocumentType = {
  /** One multi-page PDF, one page per code of the batch (primary print workflow). */
  BATCH_PDF: 'BATCH_PDF',
  /** ZIP with one PDF per code (§44, optional). */
  BATCH_ZIP: 'BATCH_ZIP',
  /** Single-page PDF for exactly one code (e.g. replacement letter). */
  CODE_PDF: 'CODE_PDF',
  /**
   * CSV export of all codes of a batch (§37, CR-WS3-2, ADR 066): generated asynchronously in parts
   * of CSV_CODES_PER_PART codes (10 ms CPU per invocation); downloaded as one concatenated file.
   */
  BATCH_CSV: 'BATCH_CSV',
} as const;
export type DocumentType = (typeof DocumentType)[keyof typeof DocumentType];
export const DOCUMENT_TYPE_VALUES = values(DocumentType);
export const DocumentTypeSchema = z.enum(DocumentType);

/** §64 + additions. Audit metadata must never contain secrets, codes, tokens or full emails. */
export const AuditEventType = {
  // §64
  COMPANY_CREATED: 'COMPANY_CREATED',
  COMPANY_UPDATED: 'COMPANY_UPDATED',
  BATCH_CREATED: 'BATCH_CREATED',
  CODE_DISABLED: 'CODE_DISABLED',
  CODE_ENABLED: 'CODE_ENABLED',
  REGISTRATION_STARTED: 'REGISTRATION_STARTED',
  EMAIL_VERIFIED: 'EMAIL_VERIFIED',
  PROVISIONING_STARTED: 'PROVISIONING_STARTED',
  PROVISIONING_FAILED: 'PROVISIONING_FAILED',
  PROVISIONING_COMPLETED: 'PROVISIONING_COMPLETED',
  ADMIN_RETRY_STARTED: 'ADMIN_RETRY_STARTED',
  // additions required by WS0
  CODE_REPLACED: 'CODE_REPLACED',
  CODE_REVEALED: 'CODE_REVEALED',
  BATCH_DISABLED: 'BATCH_DISABLED',
  REGISTRATION_EXPIRED: 'REGISTRATION_EXPIRED',
  REGISTRATION_CANCELLED: 'REGISTRATION_CANCELLED',
  EMAIL_CHANGED: 'EMAIL_CHANGED',
  VERIFICATION_RESENT: 'VERIFICATION_RESENT',
  EMAIL_BOUNCED: 'EMAIL_BOUNCED',
  ADMIN_LOGIN: 'ADMIN_LOGIN',
  ADMIN_LOGOUT: 'ADMIN_LOGOUT',
  ADMIN_CREATED: 'ADMIN_CREATED',
  RECONCILIATION_FIXED: 'RECONCILIATION_FIXED',
  RECONCILIATION_WARNING: 'RECONCILIATION_WARNING',
  ADMIN_RESEND_VERIFICATION: 'ADMIN_RESEND_VERIFICATION',
  DOCUMENT_GENERATED: 'DOCUMENT_GENERATED',
  // further additions so that every admin/system action is auditable
  COMPANY_ARCHIVED: 'COMPANY_ARCHIVED',
  COMPANY_UNARCHIVED: 'COMPANY_UNARCHIVED',
  BATCH_GENERATION_COMPLETED: 'BATCH_GENERATION_COMPLETED',
  EMAIL_COMPLAINED: 'EMAIL_COMPLAINED',
  ADMIN_UPDATED: 'ADMIN_UPDATED',
  ADMIN_DEACTIVATED: 'ADMIN_DEACTIVATED',
  ADMIN_REACTIVATED: 'ADMIN_REACTIVATED',
  DOCUMENT_REQUESTED: 'DOCUMENT_REQUESTED',
  DOCUMENT_FAILED: 'DOCUMENT_FAILED',
  SHEET_SYNC_REQUESTED: 'SHEET_SYNC_REQUESTED',
  SHEET_SYNCED: 'SHEET_SYNCED',
  JOB_DEAD: 'JOB_DEAD',
  JOB_RETRY_STARTED: 'JOB_RETRY_STARTED',
  RECONCILIATION_FINDING_RESOLVED: 'RECONCILIATION_FINDING_RESOLVED',
} as const;
export type AuditEventType = (typeof AuditEventType)[keyof typeof AuditEventType];
export const AUDIT_EVENT_TYPE_VALUES = values(AuditEventType);
export const AuditEventTypeSchema = z.enum(AuditEventType);

export const ActorType = {
  SYSTEM: 'SYSTEM',
  ADMIN: 'ADMIN',
  PARTICIPANT: 'PARTICIPANT',
  INTEGRATION: 'INTEGRATION',
} as const;
export type ActorType = (typeof ActorType)[keyof typeof ActorType];
export const ACTOR_TYPE_VALUES = values(ActorType);
export const ActorTypeSchema = z.enum(ActorType);

/** Entity referenced by `audit_events.entity_type` and `reconciliation_findings.entity_type`. */
export const EntityType = {
  COMPANY: 'COMPANY',
  BATCH: 'BATCH',
  ACTIVATION_CODE: 'ACTIVATION_CODE',
  REGISTRATION: 'REGISTRATION',
  ADMIN_USER: 'ADMIN_USER',
  INTEGRATION_JOB: 'INTEGRATION_JOB',
  GENERATED_DOCUMENT: 'GENERATED_DOCUMENT',
  SYSTEM: 'SYSTEM',
} as const;
export type EntityType = (typeof EntityType)[keyof typeof EntityType];
export const ENTITY_TYPE_VALUES = values(EntityType);
export const EntityTypeSchema = z.enum(EntityType);

/** §134 – classification of integration errors. */
export const ProvisioningErrorKind = {
  RETRYABLE: 'RETRYABLE',
  PERMANENT: 'PERMANENT',
} as const;
export type ProvisioningErrorKind =
  (typeof ProvisioningErrorKind)[keyof typeof ProvisioningErrorKind];
export const PROVISIONING_ERROR_KIND_VALUES = values(ProvisioningErrorKind);
export const ProvisioningErrorKindSchema = z.enum(ProvisioningErrorKind);

/** Outcome of one provisioning attempt (`ProvisioningResult.outcome`). */
export const ProvisioningOutcome = {
  SUCCESS: 'SUCCESS',
  RETRYABLE_ERROR: 'RETRYABLE_ERROR',
  PERMANENT_ERROR: 'PERMANENT_ERROR',
} as const;
export type ProvisioningOutcome = (typeof ProvisioningOutcome)[keyof typeof ProvisioningOutcome];
export const PROVISIONING_OUTCOME_VALUES = values(ProvisioningOutcome);
export const ProvisioningOutcomeSchema = z.enum(ProvisioningOutcome);

/** §108/§144 – kinds of inconsistencies detected by the reconciliation cron. */
export const ReconciliationFindingType = {
  /**
   * Registration EMAIL_VERIFIED or PROVISIONING for longer than STUCK_THRESHOLD_MS without an
   * active provisioning job (CR-WS7-2).
   */
  STUCK_PROVISIONING: 'STUCK_PROVISIONING',
  /** Registration ACTIVE but code not REDEEMED. */
  ACTIVE_REGISTRATION_CODE_NOT_REDEEMED: 'ACTIVE_REGISTRATION_CODE_NOT_REDEEMED',
  /** Code REDEEMED but its current registration is not ACTIVE. */
  REDEEMED_CODE_REGISTRATION_NOT_ACTIVE: 'REDEEMED_CODE_REGISTRATION_NOT_ACTIVE',
  /** Job DISPATCHED/RUNNING for longer than STUCK_THRESHOLD_MS. */
  STUCK_JOB: 'STUCK_JOB',
  /** Batch GENERATING for longer than STUCK_THRESHOLD_MS. */
  STUCK_BATCH_GENERATION: 'STUCK_BATCH_GENERATION',
  /** Queue message failed schema validation (acked, never retried). */
  INVALID_QUEUE_MESSAGE: 'INVALID_QUEUE_MESSAGE',
  /** Message arrived in the dead letter queue. */
  DEAD_LETTER: 'DEAD_LETTER',
} as const;
export type ReconciliationFindingType =
  (typeof ReconciliationFindingType)[keyof typeof ReconciliationFindingType];
export const RECONCILIATION_FINDING_TYPE_VALUES = values(ReconciliationFindingType);
export const ReconciliationFindingTypeSchema = z.enum(ReconciliationFindingType);

export const ReconciliationFindingStatus = {
  OPEN: 'OPEN',
  /** Repaired automatically by the reconciliation cron (§144 „automatisch reparieren“). */
  AUTO_FIXED: 'AUTO_FIXED',
  /** Marked as resolved by an admin. */
  RESOLVED: 'RESOLVED',
} as const;
export type ReconciliationFindingStatus =
  (typeof ReconciliationFindingStatus)[keyof typeof ReconciliationFindingStatus];
export const RECONCILIATION_FINDING_STATUS_VALUES = values(ReconciliationFindingStatus);
export const ReconciliationFindingStatusSchema = z.enum(ReconciliationFindingStatus);

export const FindingSeverity = {
  INFO: 'INFO',
  WARNING: 'WARNING',
  ERROR: 'ERROR',
} as const;
export type FindingSeverity = (typeof FindingSeverity)[keyof typeof FindingSeverity];
export const FINDING_SEVERITY_VALUES = values(FindingSeverity);
export const FindingSeveritySchema = z.enum(FindingSeverity);

/** Why a verification email is sent (payload of SEND_VERIFICATION_EMAIL jobs). */
export const VerificationEmailReason = {
  REGISTRATION_STARTED: 'REGISTRATION_STARTED',
  RESEND_REQUESTED: 'RESEND_REQUESTED',
  EMAIL_CHANGED: 'EMAIL_CHANGED',
  ADMIN_RESEND: 'ADMIN_RESEND',
} as const;
export type VerificationEmailReason =
  (typeof VerificationEmailReason)[keyof typeof VerificationEmailReason];
export const VERIFICATION_EMAIL_REASON_VALUES = values(VerificationEmailReason);
export const VerificationEmailReasonSchema = z.enum(VerificationEmailReason);

/** Why a provisioning job was created (payload of PROVISION_LEARNINGSUITE jobs). */
export const ProvisioningTrigger = {
  EMAIL_VERIFIED: 'EMAIL_VERIFIED',
  ADMIN_RETRY: 'ADMIN_RETRY',
  RECONCILIATION: 'RECONCILIATION',
} as const;
export type ProvisioningTrigger = (typeof ProvisioningTrigger)[keyof typeof ProvisioningTrigger];
export const PROVISIONING_TRIGGER_VALUES = values(ProvisioningTrigger);
export const ProvisioningTriggerSchema = z.enum(ProvisioningTrigger);

/** Admin authentication provider (§148). v1 only supports magic links (ADR 014). */
export const AuthProvider = {
  MAGIC_LINK: 'magic-link',
} as const;
export type AuthProvider = (typeof AuthProvider)[keyof typeof AuthProvider];
export const AUTH_PROVIDER_VALUES = values(AuthProvider);
export const AuthProviderSchema = z.enum(AuthProvider);

/** Deployment environment (`ENVIRONMENT` var). */
export const AppEnvironment = {
  LOCAL: 'local',
  TEST: 'test',
  STAGING: 'staging',
  PRODUCTION: 'production',
} as const;
export type AppEnvironment = (typeof AppEnvironment)[keyof typeof AppEnvironment];
export const APP_ENVIRONMENT_VALUES = values(AppEnvironment);
export const AppEnvironmentSchema = z.enum(AppEnvironment);

/** PDF template versions (§45). Old versions are never changed or removed. */
export const TemplateVersion = {
  WELCOME_LETTER_V1: 'welcome-letter-v1',
} as const;
export type TemplateVersion = (typeof TemplateVersion)[keyof typeof TemplateVersion];
export const TEMPLATE_VERSION_VALUES = values(TemplateVersion);
export const TemplateVersionSchema = z.enum(TemplateVersion);
